import { useDispatch } from 'react-redux';
import {
	Col,
	Alert,
	Spinner,
	Card,
	CardHeader,
	CardBody,
	Row,
	Modal,
	ModalHeader,
	ModalBody,
	Button,
	FormFeedback,
	Input,
	Label,
} from 'reactstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { createSelector } from 'reselect';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	getPaymentList,
	generatePayment,
	calculateModuleUsageFact,
	previewPayment,
	getRolesList,
} from '../../../slices/thunks';
import { clearPaymentPreview } from '../../../slices/payment/reducer';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import AddEditPaymentModal from './Add_Edit/AddEditPaymentModal';
import DeletePaymentModal from './Delete/DeletePaymentModal';
import PaymentDetail from './PaymentDetail';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { Icons } from '../../../utils/Icons';
import {
	generatePaymentSchema,
	formatAmericaSantiagoPeriodHint,
} from './generatePaymentSchema';
import { readPaymentGenerateQuery } from './paymentGenerateQuery';

const PaymentList = () => {
	const dispatch = useDispatch();
	const history = useNavigate();
	const [searchParams] = useSearchParams();
	const appliedGenerateQueryRef = useRef(false);

	const selectorPayments = createSelector(
		state => state.Payments,
		payment => ({
			paymentListFiltered: payment.paymentListFiltered,
			isLoadingList: payment.isLoadingList,
			isPaymentSuccess: payment.isPaymentSuccess,
			error: payment.error,
			isAdding: payment.isAdding,
			isAdded: payment.isAdded,
			isAddError: payment.isAddError,
			isUpdating: payment.isUpdating,
			isUpdated: payment.isUpdated,
			isUpdateError: payment.isUpdateError,
			isDeleting: payment.isDeleting,
			isDeleted: payment.isDeleted,
			isDeleteError: payment.isDeleteError,
			paymentPreview: payment.paymentPreview,
			isCalculating: payment.isCalculating,
			isCalculateError: payment.isCalculateError,
			isPreviewLoading: payment.isPreviewLoading,
			isPreviewError: payment.isPreviewError,
		})
	);

	const selectRolesList = createSelector(
		state => state.Roles.roleListSelect,
		roleListSelect => roleListSelect
	);

	const roleListSelect = useSelector(selectRolesList);

	const {
		paymentListFiltered,
		isLoadingList,
		paymentPreview,
		isCalculating,
		isCalculateError,
		isPreviewLoading,
		isPreviewError,
	} = useSelector(selectorPayments);

	useEffect(() => {
		dispatch(getPaymentList());
	}, []);

	const [dataPayment, setDataPayment] = useState({});
	const [isEdit, setIsEdit] = useState(false);

	// Delete Payment
	const [modal, setModal] = useState(false);
	const [modalDelete, setModalDelete] = useState(false);
	//Tab
	const [activeTab, setActiveTab] = useState('1');
	const toggleTab = tab => {
		if (activeTab !== tab) {
			setActiveTab(tab);
		}
	};

	// Generate Payment Modal
	const [modalGeneratePayment, setModalGeneratePayment] = useState(false);
	const toggleGeneratePaymentModal = () => {
		setModalGeneratePayment(open => {
			const next = !open;
			if (!next) {
				dispatch(clearPaymentPreview());
			}
			return next;
		});
	};

	const generatePaymentValidation = useFormik({
		enableReinitialize: true,
		initialValues: {
			month: '',
			year: '',
			includeSma: false,
		},
		validationSchema: generatePaymentSchema,
		onSubmit: values => {
			const month = Number(values.month);
			const year = Number(values.year);
			const includeSma = values.includeSma;
			const previewReady =
				paymentPreview &&
				!isPreviewError &&
				!isCalculateError &&
				Number(paymentPreview.year) === year &&
				Number(paymentPreview.month) === month;

			if (!previewReady) {
				toast.error(
					'Calcule y revise la previsualización del servidor antes de confirmar.'
				);
				return;
			}

			dispatch(generatePayment({ month, year, category: 'REGULAR' }))
				.unwrap()
				.then(res => {
					if (includeSma) {
						return dispatch(
							generatePayment({ month, year, category: 'SMA' })
						).unwrap();
					}
					return res;
				})
				.then(() => {
					dispatch(getPaymentList());
					dispatch(clearPaymentPreview());
					toggleGeneratePaymentModal();
					generatePaymentValidation.resetForm();
					toast.success('Pagos generados exitosamente');
				})
				.catch(error => {
					toast.error(
						error?.message || 'Error al generar pagos'
					);
				});
		},
	});

	useEffect(() => {
		if (appliedGenerateQueryRef.current) {
			return;
		}
		appliedGenerateQueryRef.current = true;
		const parsed = readPaymentGenerateQuery(searchParams);
		if (!parsed.valid) {
			return;
		}
		generatePaymentValidation.setValues({
			month: parsed.month,
			year: parsed.year,
			includeSma: generatePaymentValidation.values.includeSma,
		});
		dispatch(clearPaymentPreview());
		if (parsed.openGenerar) {
			setModalGeneratePayment(true);
		}
	}, [searchParams, dispatch, generatePaymentValidation]);

	const handleCalculateAndPreview = async () => {
		const errors = await generatePaymentValidation.validateForm();
		generatePaymentValidation.setTouched({ month: true, year: true }, true);
		if (errors.month || errors.year) {
			return;
		}
		const year = Number(generatePaymentValidation.values.year);
		const month = Number(generatePaymentValidation.values.month);
		try {
			await dispatch(calculateModuleUsageFact({ year, month })).unwrap();
			await dispatch(
				previewPayment({ year, month, category: 'REGULAR' })
			).unwrap();
		} catch (error) {
			generatePaymentValidation.setStatus(
				typeof error === 'string'
					? error
					: error?.message || 'calculate_or_preview_failed'
			);
		}
	};

	const santiagoHint = formatAmericaSantiagoPeriodHint();
	const ritualBusy = isCalculating || isPreviewLoading;
	const previewReady =
		paymentPreview &&
		!isPreviewError &&
		!isCalculateError &&
		!ritualBusy &&
		Number(paymentPreview.year) ===
			Number(generatePaymentValidation.values.year) &&
		Number(paymentPreview.month) ===
			Number(generatePaymentValidation.values.month);

	function handleToggleModal() {
		setModal(!modal);
		toggleTab('1');
		// validation.resetForm();
	}

	function handleToggleModalDelete() {
		setModalDelete(!modalDelete);
	}

	// Delete Data
	const handleClickDelete = payment => {
		setDataPayment(payment);
		setModalDelete(true);
	};

	// Update Data
	const handleClickEdit = payment => {
		setDataPayment(payment);
		handleToggleModal();
	};

	const [modalPaymentDetail, setModalPaymentDetail] = useState(false);
	const togglePaymentModal = () => setModalPaymentDetail(!modalPaymentDetail);
	const handlePaymentClick = payment => {
		setDataPayment(payment);
		togglePaymentModal();
	};

	const columns = useMemo(
		() => [
			{
				header: 'Acciones',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'payment',
				columnDefType: 'display',
				size: 'auto',
				Cell: cellProps => {
					const paymentData = cellProps.row.original;

					return (
						<React.Fragment>
							<div className='d-flex'>
								<div className='flex-shrink-0'>
									<ul className='list-inline mb-0'>
										{/* <li className='list-inline-item'>
											<Link
												to='#'
												onClick={() => {
													const paymentData = cellProps.row.original;
													setIsEdit(true);
													handleClickEdit(paymentData);
												}}
											>
												<i className='ri-pencil-fill align-bottom me-2 text-muted'></i>
											</Link>
										</li> */}
										<li className='list-inline-item'>
											<Link
												to='#'
												className='remove-item-btn'
												onClick={() => {
													handleClickDelete(paymentData);
												}}
											>
												<i className='ri-delete-bin-line align-bottom me-2 text-muted'></i>
											</Link>
										</li>
									</ul>
								</div>
							</div>
						</React.Fragment>
					);
				},
			},

			{
				header: 'Estado',
				accessorKey: 'payment_status.name',
				size: 'auto',

				Cell: ({ row }) => {
					let status = row.original.payment_status;
					let bgColor = status?.bgcolor;
					let txColor = status?.txcolor;
					return (
						<div>
							<span
								className={`badge`}
								style={{ backgroundColor: bgColor, color: txColor }}
							>
								{status ? status.name : ''}
							</span>
						</div>
					);
				},
			},

			{
				header: 'Tipo',
				accessorKey: 'category',
				size: 'auto',
				Cell: ({ row }) => {
					const category = row.original.category || 'REGULAR';
					const isRegular = category === 'REGULAR';
					return (
						<span className={`badge ${isRegular ? 'bg-info' : 'bg-success'}`}>
							{isRegular ? 'Regular' : 'SMA'}
						</span>
					);
				},
			},

			{
				header: 'Número',
				accessorKey: 'code',
				size: 'auto',
				Cell: ({ row }) => (
					<span
						onClick={() => handlePaymentClick(row.original)}
						style={{
							cursor: 'pointer',
							color: 'var(--vz-primary)',
							textDecoration: 'none',
							fontWeight: 'bold',
						}}
					>
						{row.original.code}
					</span>
				),
			},
			{
				header: 'Periodo',
				accessorKey: 'description',
				size: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Fecha',
				accessorKey: 'date',
				size: 'auto',
				dataType: 'date',
			},
			{
				header: 'Rut',
				accessorKey: 'rut',
				size: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Empresa',
				accessorKey: 'company.name',
				size: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Vencimiento',
				accessorKey: 'due_date',
				size: 'auto',
				dataType: 'date',
			},
			{
				header: 'Neto',
				accessorKey: 'net_amount',
				size: 'auto',
				dataType: 'separator',
				summary: true,
			},
			{
				header: 'IVA',
				accessorKey: 'tax_amount',
				size: 'auto',
				dataType: 'separator',
				summary: true,
			},
			{
				header: 'Total',
				accessorKey: 'total_amount',
				size: 'auto',
				dataType: 'separator',
				summary: true,
			},
			{ header: 'Moneda', accessorKey: 'currency', size: 'auto' },
			{
				header: 'Establecimientos',
				accessorKey: 'establishment_count',
				dataType: 'number',
				size: 'auto',
				summary: true,
			},
			{
				header: 'Dirección',
				accessorKey: 'address',
				size: 'auto',
				visible: false,
			},
		],
		[handleClickEdit]
	);

	return (
		<React.Fragment>
			<Row>
				<Col lg={12}>
					<Card>
						{/* <CardHeader className='border-0 pb-0'>
							<div className='d-flex align-items-end justify-content-end'>
								<div
									className='flex-shrink-1'
									style={{ marginLeft: '5px' }}
								>
									<div className='d-flex flex-wrap gap-2'>
										<button
											className='btn btn-label btn-primary'
											onClick={toggleGeneratePaymentModal}
										>
											<i className={`${Icons.plus} label-icon`}></i>{' '}
											Generar Estado de Pago
										</button>
									</div>
								</div>
							</div>
						</CardHeader> */}
						<CardBody>
							<ODataGrid
								columns={columns}
								data={paymentListFiltered || []}
								isLoadingData={isLoadingList}
								nameFile={'estados_de_pago'}
								customToolbarActions={[
									{
										label: 'Generar',
										icon: Icons.plus,
										className: 'gap-1',
										onClick: () => {
											// Abrir modal para seleccionar mes y año
											toggleGeneratePaymentModal();
										},
										title: 'Generar Estado de Pago',
									}
								]}
							/>
						</CardBody>
					</Card>
				</Col>
			</Row>

			<AddEditPaymentModal
				dataPayment={dataPayment}
				isEdit={isEdit}
				isOpen={modal}
				handleToggleModal={handleToggleModal}
				activeTab={activeTab}
				toggleTab={toggleTab}
			/>
			<DeletePaymentModal
				dataPayment={dataPayment}
				isOpen={modalDelete}
				handleToggleModalDelete={handleToggleModalDelete}
				setDataPayment={setDataPayment}
			/>

			{dataPayment && (
				<PaymentDetail
					dataPayment={dataPayment}
					isOpen={modalPaymentDetail}
					toggle={togglePaymentModal}
				/>
			)}

			{/* Generate Payment Modal */}
			<Modal
				isOpen={modalGeneratePayment}
				toggle={toggleGeneratePaymentModal}
				size='lg'
				className='border-0'
				modalClassName='modal fade zoomIn'
				backdrop={'static'}
				centered={true}
				tabIndex='-1'
			>
				<ModalHeader toggle={toggleGeneratePaymentModal}>
					Generar Estado de Pago
				</ModalHeader>
				<ModalBody>
					<form
						autoComplete='off'
						className='needs-validation'
						noValidate=''
						onSubmit={e => {
							e.preventDefault();
							generatePaymentValidation.handleSubmit();
							return false;
						}}
					>
						<div className='mb-3'>
							<Label for='month-input' className='form-label'>
								Mes
							</Label>
							<Input
								type='number'
								className='form-control'
								id='month-input'
								name='month'
								placeholder='1-12'
								min='1'
								max='12'
								onChange={generatePaymentValidation.handleChange}
								onBlur={generatePaymentValidation.handleBlur}
								value={generatePaymentValidation.values.month || ''}
								invalid={
									generatePaymentValidation.touched.month &&
									generatePaymentValidation.errors.month
										? true
										: false
								}
							/>
							{generatePaymentValidation.touched.month &&
							generatePaymentValidation.errors.month ? (
								<FormFeedback type='invalid'>
									{generatePaymentValidation.errors.month}
								</FormFeedback>
							) : null}
						</div>
						<div className='mb-3'>
							<Label for='year-input' className='form-label'>
								Año
							</Label>
							<Input
								type='number'
								className='form-control'
								id='year-input'
								name='year'
								placeholder='2020-2030'
								min='2020'
								max='2030'
								onChange={generatePaymentValidation.handleChange}
								onBlur={generatePaymentValidation.handleBlur}
								value={generatePaymentValidation.values.year || ''}
								invalid={
									generatePaymentValidation.touched.year &&
									generatePaymentValidation.errors.year
										? true
										: false
								}
							/>
							{generatePaymentValidation.touched.year &&
							generatePaymentValidation.errors.year ? (
								<FormFeedback type='invalid'>
									{generatePaymentValidation.errors.year}
								</FormFeedback>
							) : null}
						</div>
						<div className='mb-3'>
							<div className='form-check'>
								<Input
									type='checkbox'
									className='form-check-input'
									id='include-sma'
									name='includeSma'
									onChange={generatePaymentValidation.handleChange}
									checked={generatePaymentValidation.values.includeSma}
								/>
								<Label for='include-sma' className='form-check-label'>
									Después de REGULAR, generar SMA con interruptores vigentes
								</Label>
							</div>
						</div>
						<div className='mb-3'>
							<Alert color='info' className='mb-2'>
								<i className='icon-info me-2'></i>
								REGULAR usa el uso del mes (hechos de uso), no los módulos
								en vivo. SMA usa interruptores vigentes del módulo SMA, no
								los hechos del mes. Periodo en America/Santiago (hoy:{' '}
								{santiagoHint}). El mes anterior no se rellena
								automáticamente.
							</Alert>
						</div>
						{(isCalculateError || isPreviewError) && (
							<Alert color='danger' className='mb-3'>
								El cálculo o la previsualización falló. La confirmación
								queda bloqueada hasta un preview exitoso del mismo periodo.
							</Alert>
						)}
						<div className='mb-3'>
							<Button
								type='button'
								color='secondary'
								className='btn-label'
								onClick={handleCalculateAndPreview}
								disabled={ritualBusy}
							>
								{ritualBusy ? (
									<Spinner size='sm' className='me-2' />
								) : (
									<i className='icon-calculator label-icon'></i>
								)}{' '}
								Calcular
							</Button>
						</div>
						{paymentPreview && (
							<div className='mb-3'>
								<Label className='form-label'>
									Previsualización del servidor
								</Label>
								<pre
									className='bg-light p-2 small mb-0'
									style={{
										maxHeight: 240,
										overflow: 'auto',
									}}
								>
									{JSON.stringify(paymentPreview, null, 2)}
								</pre>
							</div>
						)}
						<div className='hstack gap-2 justify-content-end'>
							<Button
								className='btn-danger btn-label'
								onClick={toggleGeneratePaymentModal}
							>
								<i className='icon-x label-icon'></i> Cancelar
							</Button>
							<button
								type='submit'
								className='btn btn-label btn-primary'
								disabled={!previewReady}
							>
								<i className={`${Icons.plus} label-icon`}></i>{' '}
								Confirmar REGULAR
							</button>
						</div>
					</form>
				</ModalBody>
			</Modal>
		</React.Fragment>
	);
};

export default PaymentList;
